import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Lock, Package, Send, CreditCard, CheckCircle, Copy, ExternalLink, MessageCircle, Download, ArrowLeft, ArrowRight, Loader2, X } from 'lucide-react';

const API_URL = '/api';

export default function Order() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [pin, setPin] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  
  const [stock, setStock] = useState(0);
  const [minOrder, setMinOrder] = useState(50);
  const [maxOrder, setMaxOrder] = useState(1000);
  const [quantity, setQuantity] = useState(100);
  const [unitPrice, setUnitPrice] = useState(4);
  
  const [deliveryType, setDeliveryType] = useState('telegram');
  const [telegramUsername, setTelegramUsername] = useState('');
  
  const [discountCode, setDiscountCode] = useState('');
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [calculation, setCalculation] = useState(null);
  
  const [orderData, setOrderData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  const [orderStatus, setOrderStatus] = useState(null);

  useEffect(() => {
    if (pinVerified) {
      fetchStock();
      fetchPricing();
    }
  }, [pinVerified]);

  useEffect(() => {
    if (orderData?.expires_at && step === 5) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const expires = new Date(orderData.expires_at).getTime();
        const diff = Math.max(0, Math.floor((expires - now) / 1000));
        setTimeLeft(diff);
        if (diff <= 0) {
          clearInterval(interval);
          setError('Ödeme süresi doldu');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [orderData, step]);

  useEffect(() => {
    if (orderData?.order_code && step === 5) {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(API_URL + '/order/status/' + orderData.order_code);
          const data = await res.json();
          if (data.success && data.order) {
            setOrderStatus(data.order);
            if (data.order.status === 'paid' || data.order.status === 'delivered') {
              setError('');
              setOrderStatus(data.order);
              setStep(6);
              clearInterval(interval);
            } else if (data.order.status === 'expired' || data.order.status === 'cancelled') {
              setError('Sipariş iptal edildi veya süresi doldu');
              clearInterval(interval);
            }
          }
        } catch (e) {}
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [orderData, step]);

  const fetchStock = async () => {
    try {
      const res = await fetch(API_URL + '/order/stock');
      const data = await res.json();
      if (data.success) {
        setStock(data.stock);
        setMinOrder(data.min_order);
        setMaxOrder(data.max_order);
      }
    } catch (e) {}
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(API_URL + '/order/pricing');
      const data = await res.json();
      if (data.success) setUnitPrice(data.unit_price);
    } catch (e) {}
  };

  const verifyPin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/order/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (data.success) {
        setPinVerified(true);
        setStep(2);
      } else {
        setError('Geçersiz PIN');
        setPin('');
      }
    } catch (e) {
      setError('Bağlantı hatası');
    }
    setLoading(false);
  };

  const calculatePrice = useCallback(async () => {
    try {
      const res = await fetch(API_URL + '/order/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity, discount_code: discountCode })
      });
      const data = await res.json();
      if (data.success) setCalculation(data);
    } catch (e) {}
  }, [quantity, discountCode]);

  useEffect(() => {
    if (step === 4) calculatePrice();
  }, [step, calculatePrice]);

  const createOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/order/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity,
          discount_code: discountCode,
          delivery_type: deliveryType,
          telegram_username: telegramUsername.replace('@', '')
        })
      });
      const data = await res.json();
      if (data.success) {
        setOrderData(data);
        setStep(5);
      } else {
        setError(data.error || 'Sipariş oluşturulamadı');
      }
    } catch (e) {
      setError('Bağlantı hatası');
    }
    setLoading(false);
  };

  const submitPayment = async () => {
    if (!txHash.trim()) {
      setError('Lütfen TX Hash girin');
      return;
    }
    setSubmittingPayment(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/order/submit-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_code: orderData.order_code,
          tx_hash: txHash.trim()
        })
      });
      const data = await res.json();
      if (data.success) {
        if (data.verified && data.status === 'delivered') {
          setOrderStatus({ status: 'delivered', download_token: data.download_token, delivery_type: deliveryType, telegram_username: telegramUsername });
          setStep(6);
        } else {
          setError('TX hash gönderildi. Onay bekleniyor...');
        }
      } else {
        setError(data.error || 'Bir hata oluştu');
      }
    } catch (e) {
      setError('Bağlantı hatası');
    }
    setSubmittingPayment(false);
    setShowPaymentModal(false);
  };

  const cancelOrder = () => {
    setShowCancelModal(false);
    setStep(1);
    setPin('');
    setPinVerified(false);
    setOrderData(null);
    setOrderStatus(null);
    setTxHash('');
    setError('');
  };

  const copyWallet = () => {
    navigator.clipboard.writeText(orderData?.wallet || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m + ':' + s.toString().padStart(2, '0');
  };

  const quickAmounts = [50, 100, 200, 500];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">POSE MONSTER</h1>
          <p className="text-gray-500 text-sm mt-1">Data Market</p>
        </div>

        <div className="flex justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div key={s} className={'w-2 h-2 rounded-full transition-all duration-300 ' + (s === step ? 'w-8 bg-cyan-500' : s < step ? 'bg-cyan-500/50' : 'bg-gray-700')} />
          ))}
        </div>

        <div className="bg-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-6 shadow-2xl relative">
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">{error}</div>
          )}

          {step === 1 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Giriş PIN</h2>
              <p className="text-gray-400 text-sm mb-6">4 haneli PIN kodunuzu girin</p>
              
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && verifyPin()}
                placeholder="••••"
                className="w-40 mx-auto px-4 py-4 bg-gray-700/50 border-2 border-gray-600 rounded-xl text-white text-center text-3xl tracking-widest focus:border-cyan-500 focus:outline-none mb-6 block"
                autoFocus
              />

              <button
                onClick={verifyPin}
                disabled={pin.length !== 4 || loading}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Giriş Yap'}
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Adet Seçin</h2>
                  <p className="text-gray-400 text-sm">Stok: {stock.toLocaleString()} adet</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 mb-4">
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setQuantity(amt)}
                    className={'py-3 rounded-xl font-semibold transition-all ' + (quantity === amt ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50')}
                  >
                    {amt}
                  </button>
                ))}
              </div>

              <div className="mb-6">
                <label className="text-gray-400 text-sm mb-2 block">Veya manuel girin:</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.min(maxOrder, Math.max(minOrder, parseInt(e.target.value) || minOrder)))}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-center text-xl font-semibold focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div className="bg-gray-700/30 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-gray-400">
                  <span>Birim Fiyat:</span>
                  <span className="text-white font-semibold">${unitPrice}</span>
                </div>
                <div className="flex justify-between text-gray-400 mt-2">
                  <span>Toplam:</span>
                  <span className="text-cyan-400 font-bold text-xl">${(quantity * unitPrice).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => setStep(3)}
                disabled={quantity < minOrder || quantity > stock}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                İleri <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                  <Send className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Teslimat</h2>
                  <p className="text-gray-400 text-sm">Nasıl teslim edelim?</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => setDeliveryType('telegram')}
                  className={'w-full p-4 rounded-xl border-2 text-left transition-all ' + (deliveryType === 'telegram' ? 'border-cyan-500 bg-cyan-500/10' : 'border-gray-700 hover:border-gray-600')}
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle className={'w-6 h-6 ' + (deliveryType === 'telegram' ? 'text-cyan-400' : 'text-gray-400')} />
                    <div>
                      <div className="text-white font-semibold">Telegram <span className="ml-2 text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">Önerilen</span></div>
                      <div className="text-gray-500 text-sm">Excel dosyası Telegram'a gönderilir</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setDeliveryType('download')}
                  className={'w-full p-4 rounded-xl border-2 text-left transition-all ' + (deliveryType === 'download' ? 'border-cyan-500 bg-cyan-500/10' : 'border-gray-700 hover:border-gray-600')}
                >
                  <div className="flex items-center gap-3">
                    <Download className={'w-6 h-6 ' + (deliveryType === 'download' ? 'text-cyan-400' : 'text-gray-400')} />
                    <div>
                      <div className="text-white font-semibold">Direkt İndir</div>
                      <div className="text-gray-500 text-sm">Ödeme sonrası anında indirin</div>
                    </div>
                  </div>
                </button>
              </div>

              {deliveryType === 'telegram' && (
                <div className="mb-6">
                  <label className="text-gray-400 text-sm mb-2 block">Telegram Kullanıcı Adı:</label>
                  <div className="flex">
                    <span className="px-4 py-3 bg-gray-700 border border-gray-600 border-r-0 rounded-l-xl text-gray-400">@</span>
                    <input
                      type="text"
                      value={telegramUsername}
                      onChange={(e) => setTelegramUsername(e.target.value.replace('@', ''))}
                      placeholder="username"
                      className="flex-1 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-r-xl text-white focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setStep(4)}
                  disabled={deliveryType === 'telegram' && !telegramUsername}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
                >
                  İleri <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Sipariş Özeti</h2>
                  <p className="text-gray-400 text-sm">Kontrol edin</p>
                </div>
              </div>

              <div className="bg-gray-700/30 rounded-xl p-4 mb-4 space-y-3">
                <div className="flex justify-between text-gray-400"><span>Adet:</span><span className="text-white font-semibold">{quantity}</span></div>
                <div className="flex justify-between text-gray-400"><span>Birim Fiyat:</span><span className="text-white">${calculation?.unit_price || unitPrice}</span></div>
                {calculation?.bonus_quantity > 0 && (
                  <div className="flex justify-between text-orange-400"><span>🎁 Hediye (+{calculation.bonus_rate}%):</span><span className="font-semibold">+{calculation.bonus_quantity} adet</span></div>
                )}
                <div className="flex justify-between text-gray-400"><span>Toplam Teslim:</span><span className="text-emerald-400 font-semibold">{calculation?.total_with_bonus || quantity} adet</span></div>
                <div className="flex justify-between text-gray-400"><span>Teslimat:</span><span className="text-white">{deliveryType === 'telegram' ? '@' + telegramUsername : 'İndir'}</span></div>
                <hr className="border-gray-600" />
                <div className="flex justify-between"><span className="text-white font-semibold">Ödeme:</span><span className="text-cyan-400 font-bold text-2xl">${calculation?.total || (quantity * unitPrice).toFixed(2)}</span></div>
              </div>

              {calculation?.discount_info && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                  ✅ {calculation.discount_info} uygulandı!
                </div>
              )}

              {!showDiscountInput && !calculation?.discount_info ? (
                <button onClick={() => setShowDiscountInput(true)} className="text-gray-500 text-sm hover:text-gray-400 mb-4 block">İndirim kodunuz mu var?</button>
              ) : !calculation?.discount_info && (
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Kod"
                    className="flex-1 px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  />
                  <button onClick={calculatePrice} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-lg">Uygula</button>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep(3)} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl"><ArrowLeft className="w-5 h-5" /></button>
                <button
                  onClick={createOrder}
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Ödemeye Geç <ArrowRight className="w-5 h-5 inline" /></span>}
                </button>
              </div>
            </div>
          )}

          {step === 5 && orderData && (
            <div className="text-center">
              <button
                onClick={() => setShowCancelModal(true)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>

              <div className={'inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4 ' + (timeLeft > 60 ? 'bg-cyan-500/20 text-cyan-400' : 'bg-red-500/20 text-red-400')}>
                ⏱️ {formatTime(timeLeft)}
              </div>

              <h2 className="text-xl font-semibold text-white mb-2">Ödeme Bekliyor</h2>
              <p className="text-cyan-400 font-bold text-3xl mb-4">${orderData.total} USDT</p>

              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCodeSVG value={orderData.wallet} size={140} level="H" />
              </div>

              <div className="bg-gray-700/30 rounded-xl p-4 mb-4">
                <p className="text-gray-400 text-sm mb-2">TRC20 Cüzdan Adresi:</p>
                <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-3">
                  <code className="flex-1 text-white text-xs break-all">{orderData.wallet}</code>
                  <button onClick={copyWallet} className="p-2 hover:bg-gray-700 rounded-lg">
                    {copied ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
                <p className="text-yellow-400 text-sm">⚠️ Sadece <strong>USDT TRC20</strong> gönderin!</p>
              </div>

              <button
                onClick={() => setShowPaymentModal(true)}
                className="w-full py-3 mb-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" /> Ödeme Yaptım
              </button>

              
              <a
                href={'https://t.me/' + (orderData.telegram_support || '').replace('@', '')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm"
              >
                Sorun mu var? <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {step === 6 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Tamamlandı! 🎉</h2>
              <p className="text-gray-400 mb-6">Sipariş: <code className="text-cyan-400">{orderData?.order_code}</code></p>

              {orderStatus?.delivery_type === 'download' && orderStatus?.download_token && (
                
                <a
                  href={API_URL + '/order/download/' + orderStatus.download_token}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl"
                >
                  <Download className="w-5 h-5" /> Excel İndir
                </a>
              )}

              {orderStatus?.delivery_type === 'telegram' && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                  <MessageCircle className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                  <p className="text-white">Telegram'a gönderildi!</p>
                  <p className="text-gray-400 text-sm">@{orderStatus.telegram_username}</p>
                </div>
              )}

              <button onClick={cancelOrder} className="mt-6 text-gray-400 hover:text-white text-sm">Yeni Sipariş</button>
            </div>
          )}

          {showPaymentModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold text-white mb-4">Ödeme Bilgileri</h3>
                
                <div className="mb-4">
                  <label className="text-gray-400 text-sm mb-2 block text-left">TX Hash (İşlem Kodu)</label>
                  <input
                    type="text"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="TronScan'dan kopyalayın..."
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-cyan-500 focus:outline-none"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl"
                  >
                    İptal
                  </button>
                  <button
                    onClick={submitPayment}
                    disabled={!txHash.trim() || submittingPayment}
                    className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
                  >
                    {submittingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Onayla'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showCancelModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm text-center">
                <h3 className="text-lg font-semibold text-white mb-2">Siparişi İptal Et?</h3>
                <p className="text-gray-400 text-sm mb-6">Bu işlem geri alınamaz.</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowCancelModal(false)} className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl">Hayır</button>
                  <button onClick={cancelOrder} className="flex-1 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-xl">Evet, İptal Et</button>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">© 2025 Pose Monster</p>
      </div>
    </div>
  );
}
